import React, { useState } from 'react';
import { gtag, install } from 'ga-gtag';

import ZAWTaskList, { ZAWTask } from './ZAWTaskList';
import ZAWTextCallout, { Callout } from './ZAWTextCallout';

import { guid } from 'helpers/guid';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { formInputsToObject } from 'helpers/HtmlHelpers';
import { AriaLiveTee } from 'components/common/AriaLive';
import { trackEvent } from 'helpers/analytics';
import useMartyContext from 'hooks/useMartyContext';
import useEffectOnce from 'hooks/useEffectOnce';
import { postZAWSubmit } from 'apis/zaw';
import { FORM_MESSAGES } from 'constants/zaw';
import { appendUTMs } from 'helpers/zaw';

import css from 'styles/components/landing/zaw/zawFormStandalone.scss';

export type IZAWFormStandaloneStatus = 'amazon' | 'error' | 'success';

interface Field {
  name: string;
  label: string;
  type: string;
  placeholder: string;
}

const fields: Field[] = [
  { name: 'name', label: 'Name', type: 'text', placeholder: 'Jane Smith' },
  { name: 'company', label: 'Company', type: 'text', placeholder: 'Where you work!' },
  { name: 'email', label: 'Email', type: 'email', placeholder: 'janesmith@example.com' },
  { name: 'phone', label: 'Phone Number', type: 'tel', placeholder: '123 - 456 - 7891' }
];

export interface IZAWFormStandaloneProps {
  slotName: string;
  slotIndex: number;
  slotDetails: {
    amazonErrorMessage?: string;
    failureMessage?: string;
    redirectUrl: string;
    heading?: string;
    tasks: ZAWTask[];
    calloutsHeading: string;
    callouts: Callout[];
  };
}

export interface IZAWFormStandaloneData {
  // pulled from formInputsToObjects types
  company: string | boolean | undefined;
  email: string | boolean | undefined;
  guid: string;
  message: string;
  name: string | boolean | undefined;
  phone: string | boolean | undefined;
  utm_campaign?: string;
  utm_channel?: string;
  utm_content?: string;
  utm_source?: string;
  utm_term?: string;
}

const contentSymphonyDefaults = {
  heading: 'Get in Touch with a Zappos at Work Team Member',
  calloutsHeadings: 'Zappos at Work Benefits',
  callouts: [
    {
      icon: 'circleSuccess',
      body: 'Easy-to-use online storefronts, built custom to your business'
    },
    {
      icon: 'circleSuccess',
      body: 'Over 3,000+ styles from top-rated brands.'
    },
    {
      icon: 'circleSuccess',
      body: 'Free & fast shipping, returns, and exchanges'
    },
    {
      icon: 'circleSuccess',
      body: '24/7 customer service for any order troubles'
    }
  ],
  tasks: [
    {
      name: 'Complete and submit the contact form'
    },
    {
      name: 'A team member will reach out to you within one business day'
    },
    {
      name: "Meet with us to discuss your team's workwear needs and ask any questions"
    }
  ]
};

export const ZAWFormStandalone = (props: IZAWFormStandaloneProps) => {
  const { slotName, slotIndex, slotDetails } = props;
  // These slot details are specifically set to match the CMS keys
  const {
    amazonErrorMessage = FORM_MESSAGES.amazon,
    failureMessage = FORM_MESSAGES.error,
    heading = contentSymphonyDefaults.heading,
    redirectUrl,
    tasks = contentSymphonyDefaults.tasks,
    callouts = contentSymphonyDefaults.callouts,
    calloutsHeading = contentSymphonyDefaults.calloutsHeadings
  } = slotDetails;

  const {
    testId,
    marketplace: {
      zaw: {
        google: { id, conversionId }
      }
    }
  } = useMartyContext();

  const contentId = `zaw-form-slot-${slotIndex}`;

  const [formStatus, setFormStatus] = useState<IZAWFormStandaloneStatus | undefined>(undefined);

  useEffectOnce(() => {
    install(id);
  });

  const onSubmit = (e: any) => {
    e.preventDefault();

    const companyName = e.target?.elements['company'].value;
    if (companyName?.toLowerCase().includes('amazon')) {
      setFormStatus('amazon');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmParameters = ['utm_source', 'utm_channel', 'utm_campaign', 'utm_term', 'utm_content'];
    const redirectUrlWithUtms = appendUTMs(redirectUrl, urlParams.toString());

    const guidId = guid();

    trackEvent('ZAW_GUID', guidId);
    trackEvent('ZAW_STANDALONE_FORM_SUBMIT');

    gtag('event', 'conversion', {
      send_to: conversionId
    });

    const { company, email, name, phone } = formInputsToObject(e.target);

    const formData = {
      company,
      phone,
      email,
      name,
      message: '- sent with LP Standalone', // TODO remove from customer portal https://code.amazon.com/packages/ZapposAtWorkCustomerPortal/blobs/1df9b556bde7be8208a63bf4e600c16629400610/--/controllers/home.js#L488-L492 then delete
      guid: guidId
    };

    for (const [utm, utmValue] of urlParams) {
      if (utmParameters.includes(utm)) {
        formData[utm as keyof typeof formData] = utmValue;
      }
    }

    postZAWSubmit(formData, setFormStatus, redirectUrlWithUtms);
  };

  return (
    <>
      <article data-slot-id={slotName} data-slotindex={slotIndex}>
        <div className={css.bg}>
          <div className={css.content}>
            <h1 className={css.heading}>{heading}</h1>
            <form data-test-id={testId('zaw-form')} className={css.interestFormContainer} onSubmit={onSubmit} id={contentId} method="post">
              {formStatus === 'error' && (
                <AriaLiveTee>
                  <p className={css.error}>{failureMessage}</p>
                </AriaLiveTee>
              )}
              {formStatus === 'amazon' && (
                <AriaLiveTee>
                  <p className={css.amazon}>{amazonErrorMessage}</p>
                </AriaLiveTee>
              )}
              <div className={css.fields}>
                <fieldset>
                  {fields.map((field: Field) => (
                    <div key={field.name} className={css.inputWrapper}>
                      <label className={css.inputWrapperLabel} htmlFor={field.name}>
                        {field.label}
                      </label>
                      <input
                        className={css.inputWrapperInput}
                        type={field.type}
                        name={field.name}
                        id={field.name}
                        placeholder={field.placeholder}
                        required
                      />
                    </div>
                  ))}
                </fieldset>
                <button className={css.submitButton} type="submit">
                  Submit
                </button>
              </div>
            </form>
            <div className={css.tasks}>
              <ZAWTaskList slotDetails={{ tasks }} />
            </div>
          </div>
        </div>
        <ZAWTextCallout slotDetails={{ heading: calloutsHeading, callouts, isReducedStyling: true }} />
      </article>
    </>
  );
};

export default withErrorBoundary('ZAWFormStandalone', ZAWFormStandalone);
